import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SocialNetworkProfileServices } from "@/services/bedrock/SocialNetworkProfileServices";
import { useSmoothedValue } from "@/utils/useSmoothedValue";
import { SocialNetworksId } from "@/interfaces";
import { useMemo } from "react";

export const useSocialProfiles = (userId: number | string | undefined) => {
  const queryClient = useQueryClient();
  const {
    data: socialProfiles = [],
    isFetching,
    isLoading,
  } = useQuery({
    queryKey: ["socialProfiles", userId],
    queryFn: () => SocialNetworkProfileServices.getProfiles(userId).then(r => r.content),
    enabled: !!userId,
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (socialProfileId: number | string) => {
      return await SocialNetworkProfileServices.getProfile(userId!!, socialProfileId);
    },
    onSuccess: (updatedProfile, socialProfileId) => {
      queryClient.setQueryData(["socialProfiles", userId], (oldProfiles: any[] | undefined) => {
        if (!oldProfiles) return [updatedProfile];

        if (oldProfiles.some(p => p.id === socialProfileId)) {
          return oldProfiles.map(profile => (profile.id === socialProfileId ? updatedProfile : profile));
        }

        const networkIndex = oldProfiles.findIndex(p => p.socialNetworkId === updatedProfile.socialNetworkId);
        if (networkIndex >= 0) {
          const newProfiles = [...oldProfiles];
          newProfiles[networkIndex] = updatedProfile;
          return newProfiles;
        }

        return [...oldProfiles, updatedProfile];
      });
    },
  });

  const removeProfileFromCache = (profileId: number | string) => {
    queryClient.setQueryData(["socialProfiles", userId], (oldProfiles: any[] | undefined) => {
      if (!oldProfiles) return oldProfiles;

      // Filter out the disconnected profile
      return oldProfiles.filter(profile => profile.id !== profileId);
    });
  };

  const clearSocialProfilesCache = () => {
    queryClient.invalidateQueries({ queryKey: ["socialProfiles", userId] }).catch(console.error);
  };

  // prevents flicker between loading and not loading
  const settledLoading = useSmoothedValue(isLoading, 500, v => !v);

  const networksActiveForPosting: SocialNetworksId[] = useMemo(() => {
    return socialProfiles
      .filter(sp => sp.isActive)
      .flatMap(sp => (sp.socialNetworkId === "IG" ? ["IG", "IG_STORY"] : [sp.socialNetworkId]));
  }, [socialProfiles]);

  return {
    socialProfiles,
    refetchProfile: updateProfileMutation.mutateAsync,
    removeProfileFromCache,
    clearSocialProfilesCache,
    networksActiveForPosting,
    isFetching,
    isLoading: settledLoading,
  };
};
